import axios from "axios";
import Links from "../constants/Links";

/**
 * Result Audit / Upload File-Management API.
 *
 * Read endpoints (records, directories, download) — ADMIN, LECTURER, or RESULT_ADMIN.
 * Destructive endpoint (delete) — ADMIN or RESULT_ADMIN.
 *
 * `filePath` arguments are paths relative to the uploads base (e.g.
 * "results/STU_2019_001/CSC101_2024_2025/2026_05_24_1716530400123.xlsx"). They
 * must resolve inside `results/` server-side; pass them back verbatim.
 */
const ResultAudit = {
    /**
     * @description List every uploaded result file (no pagination).
     * @returns {Promise<AxiosResponse<Array<{path:string,name:string,size:number,lastModified:number}>>>}
     */
    listAll: async () => {
        return axios.get(Links.resultAudit.records)
    },

    /**
     * @description List files for a course code + semester session. The server
     * substring-matches the formatted course code (`-` → `_`) and session
     * (`/` → `_`), so prefix collisions are possible — narrow client-side if needed.
     * Pass values verbatim; axios URL-encodes them. Sessions like "2023/2024"
     * round-trip cleanly via query params.
     * @param {string} courseCode e.g. "CSC-101" or "CSC_101"
     * @param {string} semesterSession e.g. "2024/2025" or "2024_2025"
     * @returns {Promise<AxiosResponse<Array<*>>>} same item shape as listAll
     */
    listByCourse: async (courseCode, semesterSession) => {
        return axios.get(Links.resultAudit.recordsByCourse, {
            params: { courseCode, semesterSession },
        })
    },

    /**
     * @description List the two-level user/course directory tree.
     * @returns {Promise<AxiosResponse<Array<{path:string,name:string,type:string,user?:string}>>>}
     */
    listDirectories: async () => {
        return axios.get(Links.resultAudit.directories)
    },

    /**
     * @description Download a file as a blob. The path is validated server-side
     * to live inside `results/` — anything else returns 400 "Access denied".
     * @param {string} filePath relative path under the uploads base
     * @returns {Promise<AxiosResponse<Blob>>}
     */
    download: async (filePath) => {
        return axios.get(Links.resultAudit.download, {
            params: { filePath },
            responseType: 'blob',
        })
    },

    /**
     * @description Delete a file. Silent on missing files (returns 200).
     * Auth: ADMIN or RESULT_ADMIN only.
     * @param {string} filePath relative path under the uploads base
     * @returns {Promise<AxiosResponse<string>>}
     */
    deleteFile: async (filePath) => {
        return axios.delete(Links.resultAudit.delete, {
            params: { filePath },
        })
    },
}

export default ResultAudit
